import React, { createContext, useContext, ReactNode } from 'react';
import useMobileView from '../hooks/use-mobile-view';

interface ResponsiveContextType {
  /** True if viewport width is below mobile breakpoint (default 768px) */
  isMobile: boolean;
  /** True if viewport width is below tablet breakpoint (default 1024px) */
  isTablet: boolean;
  /** True if viewport width is below desktop breakpoint (default 1280px) */
  isDesktop: boolean;
  /** True if viewport width is below or equal to large desktop breakpoint (default 1536px) */
  isLargeDesktop: boolean;
  /** Current breakpoint name: 'mobile' | 'tablet' | 'desktop' | 'largeDesktop' */
  breakpoint: 'mobile' | 'tablet' | 'desktop' | 'largeDesktop';
  windowWidth?: number;
}

// Default breakpoints matching Tailwind's default breakpoints
const BREAKPOINTS = {
  mobile: 768,    // sm: 640px, md: 768px
  tablet: 1024,   // lg: 1024px
  desktop: 1280,  // xl: 1280px
  largeDesktop: 1536, // 2xl: 1536px
};

// Create a context with a default value
const ResponsiveContext = createContext<ResponsiveContextType | undefined>(undefined);

interface ResponsiveProviderProps {
  children: ReactNode;
  /** Custom breakpoints object to override defaults */
  breakpoints?: typeof BREAKPOINTS;
}

/**
 * Provider component that makes responsive state available to all child components
 */
export const ResponsiveProvider: React.FC<ResponsiveProviderProps> = ({
  children,
  breakpoints = BREAKPOINTS,
}) => {
  const isMobile = useMobileView(breakpoints.mobile);
  const isTablet = useMobileView(breakpoints.tablet);
  const isDesktop = useMobileView(breakpoints.desktop);
  const isLargeDesktop = useMobileView(breakpoints.largeDesktop);
  
  // Determine current breakpoint
  let breakpoint: 'mobile' | 'tablet' | 'desktop' | 'largeDesktop';
  
  if (isMobile) {
    breakpoint = 'mobile';
  } else if (isTablet) {
    breakpoint = 'tablet';
  } else if (isDesktop) {
    breakpoint = 'desktop';
  } else {
    breakpoint = 'largeDesktop';
  }
  
  const value = {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    breakpoint,
  };
  
  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
};

/**
 * Hook to use the responsive context
 */
export const useResponsive = (): ResponsiveContextType => {
  const context = useContext(ResponsiveContext);
  if (context === undefined) {
    throw new Error('useResponsive must be used within a ResponsiveProvider');
  }
  return context;
};

export default ResponsiveContext; 